import type { InputRef } from 'antd';
import { Button, Form, Input, message } from 'antd';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '@/global';
import { usePost } from '@/shared/hook/usePost';
import AuthLayout from './AuthLayout';
import { BasicUserInfo, DoubleCheckRequest, MessageResponse } from './domain/Auth';

interface OtpFormProps {
  length?: number;
  onSubmit: (code: string) => void;
  colorBgContainer: string;
  userInfo: BasicUserInfo;
}

const OtpForm: React.FC<OtpFormProps> = ({ length = 6, onSubmit, userInfo }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const [timeLeft, setTimeLeft] = useState(60);
  const [completedCode, setCompletedCode] = useState<boolean>(false);
  const inputsRef = useRef<(InputRef | null)[]>([]);
  const sendCodeEmail = usePost<DoubleCheckRequest, MessageResponse>(buildApiUrl('/Cuenta/generate-code'));

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < length - 1) {
        inputsRef.current[index + 1]?.focus();
        setCompletedCode(false);
      }
      setCompletedCode(false);
      if (value && index === length - 1) {
        setCompletedCode(true);
      }
    }
  };

  const handleReset = () => {
    setOtp(Array(length).fill(''));
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
      setCompletedCode(false);
    }
  };

  const handleSubmit = () => {
    const code = otp.join('');
    if (code.length === length) {
      handleReset();
      onSubmit(code);
    }
  };

  const handlerGenerateCode = async () => {
    const sendEmailResponse = await sendCodeEmail.postData({
      username: userInfo.username,
      email: userInfo.email,
    });
    if (sendEmailResponse?.result) {
      handleReset();
      setTimeLeft(60);
    } else {
      message.error('No se ha podido reenviar el código. Inténtelo nuevamente más tarde');
    }
  };

  return (
    <Fragment>
      <AuthLayout
        title="Verificación en dos pasos"
        hint={
          userInfo.publicEmail
            ? `Se ha enviado el código al correo: ${userInfo.publicEmail}`
            : 'Se ha enviado el código al correo registrado en la cuenta.'
        }
      >
        <Form name="otp-form" layout="vertical" onFinish={handleSubmit}>
          <Form.Item>
            <div className="flex space-x-2 justify-center">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  maxLength={1}
                  className="otp-input"
                />
              ))}
            </div>
          </Form.Item>

          <div>
            <Button
              type="default"
              htmlType="button"
              onClick={handlerGenerateCode}
              disabled={timeLeft > 0}
              style={{ width: '100%', marginBottom: '10px' }}
            >
              Generar código {timeLeft > 0 && <span>({timeLeft}) segundos</span>}
            </Button>
          </div>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }} disabled={!completedCode}>
              Verificar
            </Button>
          </Form.Item>
        </Form>
      </AuthLayout>
    </Fragment>
  );
};

export default OtpForm;
